import { Box, Group, SimpleGrid, Stack, Text, Tooltip } from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconMinus,
} from "@tabler/icons-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  EyeExcursion,
  GazePositionKey,
  HeringFlag,
  MotilityAnalysis,
  MotilityEye,
  VerticalShootFlag,
} from "types/testTypes";
import {
  excursionColor,
  findPosition,
  formatDegrees,
  formatExcursion,
  formatPrismDioptres,
  GAZE_GRID_ORDER,
  MOTILITY_TR,
  MOTILITY_EYES,
  muscleLabelKey,
  positionLabelKey,
  positionMuscleFallbackKey,
  readExcursion,
} from "../../result/utils/motilityPresentation";
import { BlockCaption } from "./MotilitySectionHeading";

/**
 * Column widths that line the eye rows up with each other and across cells. The
 * excursion column holds five characters, the widest reading the grid produces.
 */
const EYE_LABEL_WIDTH = 20;
const MUSCLE_WIDTH = 32;
const EXCURSION_WIDTH = 52;

/** Squeezing a fixed column spills its text out of the cell rather than reflowing. */
const FIXED_COLUMN = { flexShrink: 0 } as const;

const LEGEND_SWATCHES = [
  ["underaction", excursionColor(0.25)],
  ["normal", excursionColor(1)],
  ["overaction", excursionColor(1.75)],
] as const;

const HERING_STATUS_ICON: Record<HeringFlag["status"], ReactNode> = {
  confirmed: <IconCheck size={13} color="var(--mantine-color-green-7)" />,
  noSecondaryOveraction: <IconAlertTriangle size={13} color="var(--mantine-color-yellow-7)" />,
  yokeUnmeasured: <IconMinus size={13} color="var(--mantine-color-gray-6)" />,
};

/** The two muscles in a cell are each other's yoke, so the check belongs here rather than in its own table. */
interface YokeMarkerProps {
  flag: HeringFlag;
}

function YokeMarker({ flag }: YokeMarkerProps) {
  const { t } = useTranslation();

  return (
    <Tooltip
      label={t(`${MOTILITY_TR}.hering.cellHint`, {
        muscle: t(muscleLabelKey(flag.muscle)),
        excursion: readExcursion(flag.ratio, false).label,
        yoke: t(muscleLabelKey(flag.yokeMuscle)),
        yokeExcursion: readExcursion(flag.yokeRatio, flag.yokeCappedToDuctionLimit).label,
        status: t(`${MOTILITY_TR}.hering.statuses.${flag.status}`),
      })}
      withArrow
      multiline
      w={260}
    >
      <Box style={{ display: "inline-flex", cursor: "help" }}>
        {HERING_STATUS_ICON[flag.status]}
      </Box>
    </Tooltip>
  );
}

interface ShootMarkerProps {
  shoot: VerticalShootFlag;
}

function ShootMarker({ shoot }: ShootMarkerProps) {
  const { t } = useTranslation();
  const Arrow = shoot.direction === "up" ? IconArrowUp : IconArrowDown;

  return (
    <Tooltip
      label={t(`${MOTILITY_TR}.shoots.row`, {
        eye: t(`${MOTILITY_TR}.grid.${shoot.eye === "right" ? "od" : "os"}`),
        position: t(positionLabelKey(shoot.position)),
        direction: t(`${MOTILITY_TR}.shoots.${shoot.direction}`),
        magnitude: formatDegrees(shoot.magnitudeDeg),
      })}
      withArrow
    >
      <Group gap={1} wrap="nowrap" style={{ cursor: "help" }}>
        <Arrow size={12} color="var(--mantine-color-orange-7)" />
        <Text fz="xs" c="orange.7" fw={600}>
          {formatDegrees(shoot.magnitudeDeg)}
        </Text>
      </Group>
    </Tooltip>
  );
}

/** Eye, prime mover, excursion and any flags, on one line. */
interface EyeRowProps {
  eye: MotilityEye;
  excursion: EyeExcursion;
  referenceCappedToDuctionLimit: boolean;
  flag?: HeringFlag;
  shoot?: VerticalShootFlag;
}

function EyeRow({ eye, excursion, referenceCappedToDuctionLimit, flag, shoot }: EyeRowProps) {
  const { t } = useTranslation();
  const reading = readExcursion(excursion.ratio, referenceCappedToDuctionLimit);

  const detail =
    excursion.ratio === null
      ? t(`${MOTILITY_TR}.grid.unmeasured`)
      : [
          t(`${MOTILITY_TR}.grid.excursionDetail`, {
            actual: formatDegrees(excursion.actualDeg),
            reference: formatDegrees(excursion.referenceDeg),
          }),
          reading.beyondLimit
            ? t(`${MOTILITY_TR}.grid.beyondLimitDetail`, {
                percent: formatExcursion(excursion.ratio),
              })
            : null,
        ]
          .filter(Boolean)
          .join(" ");

  return (
    <Group gap={6} wrap="nowrap">
      <Text fz="xs" c="dimmed" fw={600} w={EYE_LABEL_WIDTH} style={FIXED_COLUMN}>
        {t(`${MOTILITY_TR}.grid.${eye === "right" ? "od" : "os"}`)}
      </Text>
      {excursion.muscle ? (
        <Tooltip label={t(muscleLabelKey(excursion.muscle))} withArrow>
          <Text fz="xs" fw={700} w={MUSCLE_WIDTH} style={{ ...FIXED_COLUMN, cursor: "help" }}>
            {excursion.muscle}
          </Text>
        </Tooltip>
      ) : (
        <Box w={MUSCLE_WIDTH} style={FIXED_COLUMN} />
      )}
      <Tooltip label={detail} withArrow multiline w={230}>
        <Text
          fz="sm"
          fw={700}
          c={reading.color}
          w={EXCURSION_WIDTH}
          ta="right"
          style={{ ...FIXED_COLUMN, cursor: "help" }}
        >
          {reading.label}
        </Text>
      </Tooltip>
      {flag && <YokeMarker flag={flag} />}
      {shoot && <ShootMarker shoot={shoot} />}
    </Group>
  );
}

interface GazeCellProps {
  position: GazePositionKey;
  analysis: MotilityAnalysis;
  flagsByMuscle: Map<string, HeringFlag>;
  shootsByEye: Map<string, VerticalShootFlag>;
}

function GazeCell({ position, analysis, flagsByMuscle, shootsByEye }: GazeCellProps) {
  const { t } = useTranslation();
  const entry = findPosition(analysis, position);
  const groupKey = positionMuscleFallbackKey(position);

  const body =
    position === "primary" ? (
      <Group gap={6} wrap="nowrap">
        <Text fz="xs" fw={700} tt="uppercase" c="dimmed">
          {t(positionLabelKey("primary"))}
        </Text>
        <Text fz="sm" fw={700}>
          {formatPrismDioptres(entry?.deviationPrismDioptres)}
        </Text>
      </Group>
    ) : !entry ? (
      <Text fz="xs" c="dimmed">
        {t(`${MOTILITY_TR}.grid.notReached`)}
      </Text>
    ) : (
      <Stack gap={1}>
        {groupKey && (
          <Text fz="xs" c="dimmed" fw={600}>
            {t(groupKey)}
          </Text>
        )}
        {MOTILITY_EYES.map((eye) => (
          <EyeRow
            key={eye}
            eye={eye}
            excursion={entry[eye]}
            referenceCappedToDuctionLimit={entry.referenceCappedToDuctionLimit}
            flag={entry[eye].muscle ? flagsByMuscle.get(entry[eye].muscle) : undefined}
            shoot={shootsByEye.get(`${eye}-${position}`)}
          />
        ))}
      </Stack>
    );

  return (
    <Box
      p={6}
      style={{
        border: "1px solid var(--mantine-color-gray-3)",
        borderRadius: "var(--mantine-radius-sm)",
      }}
    >
      {body}
    </Box>
  );
}

function GridLegend() {
  const { t } = useTranslation();

  return (
    <Group gap="sm" wrap="wrap">
      {LEGEND_SWATCHES.map(([key, color]) => (
        <Group key={key} gap={4} wrap="nowrap">
          <Box w={10} h={10} bg={color} style={{ borderRadius: 2 }} />
          <Text fz="xs" c="dimmed">
            {t(`${MOTILITY_TR}.legend.${key}`)}
          </Text>
        </Group>
      ))}
      <Group gap={4} wrap="nowrap">
        <IconCheck size={13} color="var(--mantine-color-green-7)" />
        <IconAlertTriangle size={13} color="var(--mantine-color-yellow-7)" />
        <Text fz="xs" c="dimmed">
          {t(`${MOTILITY_TR}.hering.legend`)}
        </Text>
      </Group>
      <Group gap={4} wrap="nowrap">
        <IconArrowUp size={13} color="var(--mantine-color-orange-7)" />
        <Text fz="xs" c="dimmed">
          {t(`${MOTILITY_TR}.shoots.legend`)}
        </Text>
      </Group>
    </Group>
  );
}

/**
 * The clinical nine-position grid. Sized to its content so it reads as a chart
 * rather than a stretched table; the card supplies the border.
 */
interface GazePositionGridProps {
  analysis: MotilityAnalysis;
}

export default function GazePositionGrid({ analysis }: GazePositionGridProps) {
  const { t } = useTranslation();

  const flagsByMuscle = new Map(analysis.heringFlags.map((flag) => [flag.muscle, flag]));
  const shootsByEye = new Map(
    analysis.verticalShoots.map((shoot) => [`${shoot.eye}-${shoot.position}`, shoot]),
  );

  // The grid holds its intrinsic width and the findings column beside it yields.
  return (
    <Stack gap={6} style={{ flexShrink: 0 }}>
      <BlockCaption
        title={t(`${MOTILITY_TR}.grid.title`)}
        hint={`${t(`${MOTILITY_TR}.grid.helper`)} ${t(`${MOTILITY_TR}.hering.helper`)}`}
      />
      <SimpleGrid cols={3} spacing={6} w="fit-content">
        {GAZE_GRID_ORDER.map((position) => (
          <GazeCell
            key={position}
            position={position}
            analysis={analysis}
            flagsByMuscle={flagsByMuscle}
            shootsByEye={shootsByEye}
          />
        ))}
      </SimpleGrid>
      <GridLegend />
    </Stack>
  );
}
